"use client";

import React, { useEffect, useRef, useState } from 'react';
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const timelineData = [{
  year: "1980",
  title: "The Analog Era",
  description: "Our founders began their careers repairing the earliest NC and analog CNC equipment, mastering the electrical and mechanical foundations of the industry."
}, {
  year: "1995",
  title: "Digital Transition",
  description: "As machines evolved, so did our expertise. We navigated the shift to complex servo systems and early digital controls, reducing downtime for high-volume shops."
}, {
  year: "2018",
  title: "Knowledge Database",
  description: "Recognizing the demand for dependable repair resources, we began building our information database, planning to replicate our knowledge and repair abilities."
}, {
  year: "2026",
  title: "Ai Integration",
  description: "Launch of CNCWise. We codified over 45 years of hard-won diagnostic intuition into a virtual service technician, available to you 24/7."
}];
export default function ExpertiseSince1980() {
  const {
    ref: textRef,
    isVisible: textVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: timelineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="expertise-since-1980" className="relative overflow-hidden bg-background py-24 lg:py-32">
            {}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden select-none">
                <span className="text-[25rem] font-bold leading-none tracking-tighter text-foreground/[0.03] md:text-[35rem] lg:text-[45rem] dark:text-foreground/[0.02]">
                    1980
                </span>
            </div>

            <div className="container relative z-10 mx-auto max-w-7xl px-6">
                <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
                    
                    {}
                    <div ref={textRef} className={`lg:col-span-6 lg:col-start-1 transition-all duration-1000 ease-out ${textVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
                        <div className="mb-4 inline-flex items-center gap-2">
                            <span className="h-px w-8 bg-primary"></span>
                            <span className="text-sm font-semibold tracking-widest text-primary uppercase">Our HISTORY</span>
                        </div>
                        <h2 className="mb-8 font-serif text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">46 Years of Expertise, and Counting.</h2>
                        <div className="space-y-6 text-lg leading-relaxed text-muted-foreground md:text-xl">
                            <p>For over four decades, the minds behind CNCWise have been on the shop floor. Starting in 1980, our founders began repairing the earliest analog CNC machines. We've seen the evolution of manufacturing firsthand—from punch tapes to complex multi-axis machining centers.</p>
                            <p>This deep, hands-on experience is the foundation of CNCWise. We didn't just build a software platform; we codified forty-six years of hard-won diagnostic intuition into a software platform that understands your machines as well as a master technician does.</p>
                            <p>We know the cost of downtime. We understand the frustration of waiting for on-site service. This is why we built the first solution of its kind: to put expert-level troubleshooting directly in your hands.</p>
                        </div>
                    </div>

                    {}
                    <div ref={timelineRef} className={`lg:col-span-4 lg:col-start-8 transition-all duration-1000 delay-300 ease-out ${timelineVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
                        <div className="relative ml-4 border-l border-border/60 py-4 md:ml-0">
                            <div className="space-y-12">
                                {timelineData.map((item, i) => <div key={i} data-index={i} className="group relative pl-8 md:pl-10">
                                        {}
                                        <div className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary ring-4 ring-background transition-transform duration-300 group-hover:scale-150" />
                                        
                                        {}
                                        <div className="mb-1 text-sm font-bold tracking-wider text-primary uppercase">
                                            {item.year}
                                        </div>
                                        <h3 className="mb-2 text-xl font-semibold text-foreground">
                                            {item.title}
                                        </h3>
                                        <p className="text-base leading-relaxed text-muted-foreground">
                                            {item.description}
                                        </p>
                                    </div>)}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>;
}